import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { INVESTOR_CERTIFICATES_LIST_QUERY_KEY } from '@/modules/investor-dashboard/queries/certificates.ts';
import { CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import {
  BUY_BACK_BY_ID_QUERY_KEY,
  BUY_BACK_LIST_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/buyBack.tsx';
import { ISSUER_CERTIFICATES_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/certificates.ts';
import {
  ISSUED_CAPITAL_KEY,
  ISSUER_COMMUNICATION_PREFERENCES_QUERY_KEY,
  ISSUER_ISSUED_CAPITAL_OVER_TIME_CHART_KEY,
  ISSUER_PAYMENT_ELECTION_QUERY_KEY,
  ISSUER_PENDING_APPLICATION_SUMMARY_CHART_KEY,
  ISSUER_PENDING_APPLICATION_SUMMARY_SECTION_KEY,
} from '@/modules/issuer-dashboard/queries/charts.ts';
import {
  APPLICATIONS_OVER_TIME_SUMMARY_QUERY_KEY,
  ISSUED_CAPITAL_SUMMARY_KEY,
  PENDING_APPLICATIONS_KEY,
  PENDING_APPLICATIONS_SUMMARY_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/dashboard.ts';
import {
  CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY,
  DIVIDEND_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { ISSUER_DOCUMENTS_QUERY_KEY } from '@/modules/issuer-dashboard/queries/documents.ts';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { TAX_REPORT_INCOME_HOLDER_KEY } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { ISSUER_REGISTER_NAV_QUERY_KEY } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { CURRENT_ISSUER_PAYMENTS_QUERY_KEY } from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  ISSUER_PORTFOLIOS_LIST_QUERY_KEY,
  ISSUER_PORTFOLIO_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY,
  ISSUER_PORTFOLIO_CASH_MOVEMENT_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { ISSUER_REGISTER_CLASS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/registers.tsx';
import {
  CHARTS_ISSUER_ISSUED_CAPITAL_OVERTIME_QUERY_KEY,
  ISSUER_REGISTER_CLASSES_UNITS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/securities.ts';
import {
  SHAREHOLDERS_HOLDINGS_QUERY_KEY,
  SHAREHOLDER_BALANCE_QUERY_KEY,
  SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY,
  TOP_HOLDERS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY } from '@/modules/issuer-dashboard/queries/transactions.ts';
import {
  CURRENT_ISSUER_TRANSFERS_QUERY_KEY,
  CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/transfers.tsx';
import {
  GetIssuerActivitiesListResponse,
  IssuerActivitiesListV1SrActivitiesIssuerGetParams,
} from 'api';

export const CURRENT_ISSUER_ACTIVITIES_QUERY_KEY = 'vestra/issuer-current-activities';
export const CURRENT_ISSUER_TRANSACTIONS_QUERY_KEY = 'vestra/issuer-current-transactions';

export const useIssuerActivitiesListQuery = (
  payload: IssuerActivitiesListV1SrActivitiesIssuerGetParams,
  disabled = false,
  register_id?: number,
) => {
  const { page, ...data } = payload;

  const previousDataRef = useRef<InfiniteData<GetIssuerActivitiesListResponse> | null>(null);

  return useInfiniteQuery<GetIssuerActivitiesListResponse>({
    queryKey: [CURRENT_ISSUER_ACTIVITIES_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.issuerActivitiesListV1SrActivitiesIssuerGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
    refetchInterval: 1000 * 60, //1min
    onSuccess: async data => {
      const previousData = previousDataRef.current;

      if (previousData) {
        const hasStatusChanged = data.pages.some((newPage, pageIndex) =>
          newPage.data.some((newItem, itemIndex) => {
            const prevItem = previousData?.pages[pageIndex]?.data?.[itemIndex];
            return prevItem && newItem.status !== prevItem.status;
          }),
        );
        if (hasStatusChanged) {
          await invalidateQueriesTransactions({ updateDividend: false });
        }
      }

      previousDataRef.current = data;
    },
  });
};

interface InvalidateQueriesTransactionsProps {
  updateDividend?: boolean;
  updateDashboard?: boolean;
  updatePortfolio?: boolean;
}

export const invalidateQueriesTransactions = async ({
  updateDividend = true,
  updateDashboard = true,
  updatePortfolio = true,
}: InvalidateQueriesTransactionsProps = {}) => {
  if (updateDividend) {
    await queryClient.invalidateQueries({
      queryKey: [CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY],
    });
    await queryClient.invalidateQueries({ queryKey: [DIVIDEND_BY_ID_QUERY_KEY] });
  }
  if (updatePortfolio) {
    await queryClient.invalidateQueries({
      queryKey: [ISSUER_PORTFOLIOS_LIST_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [ISSUER_PORTFOLIO_BY_ID_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENTS_LIST_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [ISSUER_PORTFOLIO_CASH_MOVEMENT_BY_ID_QUERY_KEY],
    });
  }
  await queryClient.invalidateQueries({
    queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY],
  });
  await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY]);
  await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY]);
  await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSACTIONS_QUERY_KEY]);
  await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSFERS_QUERY_KEY]);
  await queryClient.invalidateQueries([BUY_BACK_LIST_QUERY_KEY]);
  await queryClient.invalidateQueries([BUY_BACK_BY_ID_QUERY_KEY]);
  await queryClient.invalidateQueries([CURRENT_ISSUER_PAYMENTS_QUERY_KEY]);
  await queryClient.invalidateQueries([ISSUER_CERTIFICATES_LIST_QUERY_KEY]);
  await queryClient.invalidateQueries([INVESTOR_CERTIFICATES_LIST_QUERY_KEY]);

  await queryClient.invalidateQueries([CURRENT_ISSUER_ACTIVITIES_QUERY_KEY]);
  await queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
  await queryClient.invalidateQueries([ISSUER_REGISTER_CLASS_LIST_QUERY_KEY]);
  await queryClient.invalidateQueries([SHAREHOLDERS_HOLDINGS_QUERY_KEY]);

  if (updateDashboard) {
    await queryClient.invalidateQueries([PENDING_APPLICATIONS_KEY]);
    await queryClient.invalidateQueries([APPLICATIONS_OVER_TIME_SUMMARY_QUERY_KEY]);
    await queryClient.invalidateQueries([PENDING_APPLICATIONS_SUMMARY_QUERY_KEY]);
    await queryClient.invalidateQueries([ISSUED_CAPITAL_SUMMARY_KEY]);

    await queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
    await queryClient.invalidateQueries([SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY]);
    await queryClient.invalidateQueries([SHAREHOLDER_BALANCE_QUERY_KEY]);

    await queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
    await queryClient.invalidateQueries([ISSUER_PENDING_APPLICATION_SUMMARY_CHART_KEY]);
    await queryClient.invalidateQueries([ISSUER_PENDING_APPLICATION_SUMMARY_SECTION_KEY]);

    await queryClient.invalidateQueries([ISSUER_ISSUED_CAPITAL_OVER_TIME_CHART_KEY]);
    await queryClient.invalidateQueries({
      queryKey: [CHARTS_ISSUER_ISSUED_CAPITAL_OVERTIME_QUERY_KEY],
    });
    await queryClient.invalidateQueries({ queryKey: [ISSUER_REGISTER_CLASSES_UNITS_QUERY_KEY] });
    await queryClient.invalidateQueries({
      queryKey: [ISSUER_COMMUNICATION_PREFERENCES_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [ISSUER_PAYMENT_ELECTION_QUERY_KEY],
    });
  }

  await queryClient.invalidateQueries({ queryKey: [TAX_REPORT_INCOME_HOLDER_KEY] });
  await queryClient.invalidateQueries({
    queryKey: [NOTIFICATIONS_LIST_QUERY_KEY],
  });
  await queryClient.invalidateQueries([ISSUER_DOCUMENTS_QUERY_KEY]);
};
